import { unwrapObject, getAccessToken, toBase64 } from './apiUtils';
import { getWarehouseId, getTerminalToken } from './TerminalService';
import { call, toNumber } from './SalesService';

/**
 * Sesion de caja: la jornada de una caja, de la apertura al cierre. Sustituye
 * a lo que el arqueo guardaba en localStorage (fondos_apertura_hoy y
 * cierre_caja_logs). Lo esperado por medio de pago lo calcula el servidor a
 * partir de los tickets de la sesion; el frontal solo aporta lo contado.
 */
const ROUTES = {
    current: '/cash_session_current',
    open: '/cash_session_open',
    close: '/cash_session_close'
};

export const CASH_CHANNELS = ['cash', 'card', 'marcial_card', 'gift_card', 'invoice'];

export interface CashChannelTotals {
    channel: string;
    /** Ventas - devoluciones de la sesion. */
    net: number;
    /** Lo que deberia haber: en efectivo incluye el fondo de apertura. */
    expected: number;
    salesCount: number;
    returnsCount: number;
}

export interface CashSession {
    id: string;
    openingCash: number;
    openedAt: Date;
    channels: CashChannelTotals[];
}

const emptyChannels = (openingCash: number): CashChannelTotals[] =>
    CASH_CHANNELS.map(channel => ({
        channel,
        net: 0,
        expected: channel === 'cash' ? openingCash : 0,
        salesCount: 0,
        returnsCount: 0
    }));

const mapSession = (raw: any): CashSession => {
    const openingCash = toNumber(raw.opening_cash);
    return {
        id: String(raw.id),
        openingCash,
        openedAt: new Date(raw.opened_at || Date.now()),
        channels: Array.isArray(raw.channels)
            ? raw.channels.map((c: any) => ({
                channel: String(c.channel),
                net: toNumber(c.net),
                expected: toNumber(c.expected),
                salesCount: toNumber(c.sales_count),
                returnsCount: toNumber(c.returns_count)
            }))
            : emptyChannels(openingCash)
    };
};

/** La sesion abierta de esta caja con sus totales en vivo, o null si no hay. */
export const getCurrentCashSession = async (): Promise<CashSession | null> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    let res: any;
    try {
        res = await call(ROUTES.current, { access_token: accessToken, warehouse_id: getWarehouseId(), terminal_token: getTerminalToken() });
    } catch (err) {
        if (String(err?.message).toLowerCase().indexOf('no abierta') !== -1) return null;
        throw err;
    }
    const raw = unwrapObject(res);
    return raw && raw.id ? mapSession(raw) : null;
};

/**
 * Abre la caja. Sin `openingCash` el servidor usa el fondo que dejo la sesion
 * anterior para hoy (o 150 la primera vez).
 */
export const openCashSession = async (openingCash?: number): Promise<CashSession> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const res = await call(ROUTES.open, {
        access_token: accessToken,
        warehouse_id: getWarehouseId(),
        // El servidor toma sociedad y almacen de la tienda del TPV.
        terminal_token: getTerminalToken(),
        opening_cash: openingCash === undefined ? undefined : openingCash.toFixed(2)
    });
    const raw = unwrapObject(res);
    if (!raw || !raw.id) throw new Error('El backend no devolvio la sesion de caja.');
    return mapSession(raw);
};

/** La sesion abierta, abriendola si todavia no lo esta. */
export const ensureCashSession = async (): Promise<CashSession> => {
    const current = await getCurrentCashSession();
    if (current) return current;

    try {
        return await openCashSession();
    } catch (err) {
        // Otra caja (o una pestana) la abrio entre medias: se lee la suya.
        const raced = await getCurrentCashSession();
        if (raced) return raced;
        throw err;
    }
};

export interface CashClosureInput {
    /** Lo contado por medio de pago. */
    counted: Record<string, number>;
    nextDayCash: number;
    cashWithdrawal: number;
    /** Cerrar aunque no cuadre. Sin esto el servidor rechaza el descuadre. */
    assumed: boolean;
}

/**
 * Cierra la caja. El servidor recalcula lo esperado y valida el cuadre: si no
 * cuadra y no se asume, responde con el motivo y la sesion sigue abierta.
 */
export const closeCashSession = async (sessionId: string, input: CashClosureInput): Promise<void> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const counted = CASH_CHANNELS.map(channel => ({
        channel,
        counted: Number((input.counted[channel] || 0).toFixed(2))
    }));

    await call(ROUTES.close, {
        access_token: accessToken,
        id: sessionId,
        next_day_cash: input.nextDayCash.toFixed(2),
        cash_withdrawal: input.cashWithdrawal.toFixed(2),
        // Solo cuando es true: el gateway convierte 'true'/'false' en booleanos
        // de Python. Omitido, el servidor usa su defecto (false).
        assumed: input.assumed ? 'true' : undefined,
        // En base64: en claro el gateway parsearia el JSON y llegaria como lista.
        counted: toBase64(JSON.stringify(counted))
    });
};
